import {
  addressFormat,
  EMPTY_ADDRESS,
  isAddressEmpty,
  type AddressValue,
} from '@/components/address/address-value';
import type { Address } from './page/api';

const part = (text: string | null | undefined): string => text?.trim() ?? '';

/**
 * A saved address as the address field shows it (#958 piece E). The contract keeps the province
 * (Italy) and the region (elsewhere) apart; the field has one box for both. An address saved as
 * text only, or as text with some parts but no street (the picker's stopgap before the field, an
 * import), shows its text in «Via», so nothing written is lost; such an address is not a place
 * the field filled, so it comes without its place id (sent again only when the person changes it). The coordinates stay with the saved address (see `addressOf`): the field's
 * `coordinates` carry the time they may be kept until, which a saved address has not. `country`,
 * the supplier's, is the country of an address not written yet or saved without one.
 */
export function addressValueOf(
  address: Address | null | undefined,
  country?: string | null,
): AddressValue {
  const fallback = part(country) || EMPTY_ADDRESS.countryCode;
  if (address === null || address === undefined) {
    return { ...EMPTY_ADDRESS, countryCode: fallback };
  }
  const countryCode = part(address.country) || fallback;
  const province =
    countryCode === 'IT'
      ? part(address.province) || part(address.region)
      : part(address.region) || part(address.province);
  const value: AddressValue = {
    street: part(address.street),
    streetNumber: part(address.streetNumber),
    postalCode: part(address.postalCode),
    city: part(address.city),
    province,
    countryCode,
    placeId: address.placeId ?? null,
    coordinates: null,
  };
  // A saved text the parts do not say (no street among them) is kept in «Via», whole. It is the
  // person's words, not Google's parts: the field does not show it as filled from a place.
  const text = part(address.text);
  return value.street === '' && text !== '' && text !== addressText(value)
    ? { ...value, street: text, placeId: null }
    : value;
}

/** The address as one line, in the country's order: what the person confirmed, as `text`. */
export function addressText(value: AddressValue): string {
  const format = addressFormat(value.countryCode);
  const [street, number] = [value.street.trim(), value.streetNumber.trim()];
  const streetLine = (format.numberFirst ? [number, street] : [street, number])
    .filter(Boolean)
    .join(' ');
  const [postal, city, province] = [
    value.postalCode.trim(),
    value.city.trim(),
    value.province.trim(),
  ];
  const town = (format.postalCodeAfterCity ? [city, province, postal] : [postal, city, province])
    .filter(Boolean)
    .join(' ');
  return [streetLine, town].filter(Boolean).join(', ');
}

/**
 * The saved text `addressValueOf` put in «Via» (a text whose parts have no street), when «Via»
 * still holds it; null otherwise.
 */
function legacyText(value: AddressValue, before: Address | null | undefined): string | null {
  if (before === null || before === undefined || part(before.street) !== '') return null;
  const text = part(before.text);
  return text !== '' && value.street.trim() === text ? text : null;
}

/**
 * The field's address as the contract takes it; null when nothing is written. `before`, the saved
 * address, gives back its coordinates while the place is still the same one (a house number added
 * by hand keeps the place, board 2.4); a place left by hand takes them away with its id. While
 * «Via» still holds a saved text (review 236), that text goes back unchanged and is not a street:
 * the line is not rebuilt from it, which would repeat the city and the postal code it already has.
 */
export function addressOf(value: AddressValue, before?: Address | null): Address | null {
  if (isAddressEmpty(value)) return null;
  const orNull = (text: string) => (text.trim() === '' ? null : text.trim());
  const italian = value.countryCode === 'IT';
  const keptCoordinates =
    value.coordinates === null && value.placeId !== null && before?.placeId === value.placeId
      ? { latitude: before.latitude ?? null, longitude: before.longitude ?? null }
      : null;
  const legacy = legacyText(value, before);
  return {
    text: legacy ?? addressText(value),
    street: legacy === null ? orNull(value.street) : null,
    streetNumber: orNull(value.streetNumber),
    postalCode: orNull(value.postalCode),
    city: orNull(value.city),
    province: italian ? orNull(value.province) : null,
    region: italian ? null : orNull(value.province),
    country: orNull(value.countryCode),
    placeId: value.placeId,
    latitude: value.coordinates?.latitude ?? keptCoordinates?.latitude ?? null,
    longitude: value.coordinates?.longitude ?? keptCoordinates?.longitude ?? null,
  };
}

/** An address taken away: every part null, as an update sends it. */
export const NO_ADDRESS: Address = {
  text: null,
  street: null,
  streetNumber: null,
  postalCode: null,
  city: null,
  province: null,
  region: null,
  country: null,
  placeId: null,
  latitude: null,
  longitude: null,
};

const PARTS = ['street', 'streetNumber', 'postalCode', 'city', 'province', 'countryCode'] as const;

/**
 * Whether the field still says what was saved: then a form sends nothing for it. `country`, the
 * supplier's, is what the field showed for an address saved without one: a «Paese» chosen since is
 * a change.
 */
export function sameAddress(
  value: AddressValue,
  before: Address | null | undefined,
  country?: string | null,
): boolean {
  const saved = addressValueOf(before, country);
  if (isAddressEmpty(value) && isAddressEmpty(saved)) return true;
  return (
    PARTS.every((key) => value[key].trim() === saved[key].trim()) && value.placeId === saved.placeId
  );
}

/** What an update sends for the field: the address, or `NO_ADDRESS` when it was emptied. */
export function addressUpdateOf(value: AddressValue, before: Address | null | undefined): Address {
  return addressOf(value, before) ?? NO_ADDRESS;
}
